/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  defaultFormOptions,
  ReactHookFormProvider,
} from '@opengeoweb/form-fields';
import {
  ConfirmationButtons,
  CustomDialog,
  usePrevious,
} from '@opengeoweb/shared';
import WorkspacePresetsForm from './WorkspacePresetsForm';
import type {
  WorkspacePresetForBE,
  WorkspaceViewsFromBE,
} from '../../store/workspace/types';
import { WorkspacePresetAction } from '../../store/workspace/types';
import type {
  WorkspaceListError,
  WorkspacePresetFormValues,
} from '../../store/workspaceList/types';
import { useWorkspaceTranslation } from '../../utils/i18n';

export interface WorkspacePresetsFormDialogProps {
  isOpen: boolean;
  action: WorkspacePresetAction;
  presetId: string;
  formValues: WorkspacePresetFormValues;
  onFormSubmit: (
    action: WorkspacePresetAction,
    presetId: string,
    data: WorkspacePresetForBE,
  ) => void;
  onClose: () => void;
  isLoading: boolean;
  error?: WorkspaceListError;
  isWorkspaceSaved?: boolean;
}

const WorkspacePresetsFormDialog: React.FC<WorkspacePresetsFormDialogProps> = ({
  isOpen,
  action,
  presetId,
  formValues,
  onClose,
  onFormSubmit,
  isLoading = false,
  error,
  isWorkspaceSaved = false,
}: WorkspacePresetsFormDialogProps) => {
  const { handleSubmit: handleFormSubmit, reset } =
    useFormContext<WorkspacePresetForBE>();
  const { t } = useWorkspaceTranslation();
  const wasOpen = usePrevious(isOpen);

  React.useEffect(() => {
    if (isOpen && wasOpen !== isOpen) {
      reset(formValues);
    }
  }, [formValues, reset, isOpen, wasOpen]);

  const getConfirmLabel = (action: WorkspacePresetAction): string => {
    switch (action) {
      case WorkspacePresetAction.DELETE:
      case WorkspacePresetAction.DELETE_SYSTEM_PRESET: {
        return t('workspace-delete');
      }
      case WorkspacePresetAction.RESET: {
        return t('workspace-reset');
      }
      default: {
        return t('workspace-save');
      }
    }
  };

  const getDialogTitle = (action: WorkspacePresetAction): string => {
    switch (action) {
      case WorkspacePresetAction.DELETE: {
        return t('workspace-dialog-delete');
      }
      case WorkspacePresetAction.DELETE_SYSTEM_PRESET: {
        return t('workspace-dialog-delete-system');
      }
      case WorkspacePresetAction.DUPLICATE: {
        return t('workspace-duplicate');
      }
      case WorkspacePresetAction.EDIT: {
        return t('workspace-edit');
      }
      case WorkspacePresetAction.EDIT_TITLE: {
        return t('workspace-edit-title');
      }
      case WorkspacePresetAction.SAVE: {
        return t('workspace-admin-save-user-workspace');
      }
      case WorkspacePresetAction.SAVE_SYSTEM_PRESET: {
        return t('workspace-admin-save-workspace');
      }
      case WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS: {
        return t('workspace-save-as');
      }
      case WorkspacePresetAction.RESET: {
        return t('workspace-reset-title');
      }
      default: {
        return t('workspace-save-as');
      }
    }
  };

  const confirmLabel = getConfirmLabel(action);

  const onSubmit = async (): Promise<void> => {
    await handleFormSubmit((rawValues: WorkspacePresetForBE): void => {
      // rawValues can be WorkspacePresetForBE OR WorkspaceFormValues
      const { title, description, abstract, views: viewEdits } = rawValues;

      // Start from the full BE shape already in the form (it contains the real views array)
      const base: WorkspacePresetForBE = rawValues as WorkspacePresetForBE;

      // Normalize workspace title/abstract
      const trimmedWorkspaceTitle =
        typeof title === 'string' ? title.trim() : base.title;
      const trimmedWorkspaceAbstract =
        typeof abstract === 'string' ? abstract.trim() : base.abstract;
      const trimmedWorkspaceDescription =
        typeof description === 'string' ? description.trim() : base.description;

      // Build a map of mosaicNodeId -> trimmed new title (only if present)
      const editedTitles = new Map<string, string>();
      (Array.isArray(viewEdits) ? viewEdits : []).forEach(
        (ve: WorkspaceViewsFromBE) => {
          if (ve?.mosaicNodeId && typeof ve.title === 'string') {
            editedTitles.set(ve.mosaicNodeId, ve.title.trim());
          }
        },
      );

      // Merge titles into the outgoing BE views
      const mergedViews: WorkspaceViewsFromBE[] = (base.views ?? []).map(
        (view, idx) => {
          const byKey = editedTitles.get(view.mosaicNodeId);
          if (byKey !== undefined) {
            return { ...view, title: byKey };
          }

          // (Optional) index fallback if mosaicNodeId wasn’t provided in the form
          const fallbackTitle =
            Array.isArray(viewEdits) &&
            viewEdits[idx] &&
            typeof viewEdits[idx].title === 'string'
              ? viewEdits[idx].title.trim()
              : undefined;

          return fallbackTitle !== undefined
            ? { ...view, title: fallbackTitle }
            : view;
        },
      );

      const parsedFormValues: WorkspacePresetForBE = {
        ...base,
        title: trimmedWorkspaceTitle,
        abstract: trimmedWorkspaceAbstract,
        description: trimmedWorkspaceDescription,
        views: mergedViews,
      };

      onFormSubmit(action, presetId, parsedFormValues);
    })();
  };

  const cancelLabel = t('workspace-cancel');

  const withLabelsPanel =
    action === WorkspacePresetAction.SAVE_AS ||
    action === WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS ||
    action === WorkspacePresetAction.EDIT;

  const actionsNode = withLabelsPanel ? null : (
    <ConfirmationButtons
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      isLoading={isLoading}
      onClose={onClose}
      onSubmit={onSubmit!}
    />
  );

  const dialogTitle = getDialogTitle(action);

  return (
    <CustomDialog
      actions={actionsNode}
      data-testid="workspace-preset-dialog"
      description=""
      headerActions={withLabelsPanel ? [] : undefined}
      isResizable
      key={withLabelsPanel ? 'wide' : 'narrow'}
      minWidth={withLabelsPanel ? 635 : undefined}
      onClose={onClose}
      open={isOpen}
      resizableDefaultSize={{
        width: withLabelsPanel ? 700 : 500,
        height: withLabelsPanel ? 650 : 'auto',
      }}
      sx={
        withLabelsPanel
          ? {
              '& [data-testid="dialog-action-header"]': {
                display: 'none',
              },
              '& .MuiDialogContent-root': {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
              },
            }
          : undefined
      }
      title={withLabelsPanel ? undefined : getDialogTitle(action)}
    >
      <WorkspacePresetsForm
        action={action}
        cancelLabel={cancelLabel}
        confirmLabel={confirmLabel}
        dialogTitle={dialogTitle}
        error={error}
        formValues={formValues}
        hasSavedWorkspace={isWorkspaceSaved}
        isLoading={isLoading}
        onClose={onClose}
        onSubmit={onSubmit}
        showInlineActions={withLabelsPanel}
      />
    </CustomDialog>
  );
};

const WorkspacePresetsFormDialogWrapper: React.FC<
  WorkspacePresetsFormDialogProps
> = ({ ...props }: WorkspacePresetsFormDialogProps) => (
  <ReactHookFormProvider
    options={{
      ...defaultFormOptions,
      defaultValues: props.formValues,
    }}
  >
    <WorkspacePresetsFormDialog {...props} />
  </ReactHookFormProvider>
);

export default WorkspacePresetsFormDialogWrapper;
